import { FC, ReactNode } from 'react';
import { useField } from 'formik';
import useTheme from '@mui/material/styles/useTheme';

import SelectBase, {
  SelectBaseProps,
  SelectOption,
} from 'components/SelectBase';

import { makeOption, Options } from 'lib';

export type SelectValue = {
  readonly id: string;
  readonly name: string;
};

export type SelectProps<K extends string | number = string | number> = Omit<
  SelectBaseProps,
  'value' | 'labelId' | 'options'
> & {
  options?: SelectOption<K>[];
  keys?: K[];
  optionsConfig?: Options<K>;
  name: string;
  helperText?: ReactNode;
};

const Select: FC<SelectProps> = props => {
  const {
    options = [],
    keys,
    optionsConfig,
    label,
    helperText,
    ...rest
  } = props;
  const [field, meta] = useField(props.name);
  const labelId = `${props.name}-label`;
  const theme = useTheme();

  const displayOptions =
    keys && optionsConfig
      ? keys.map(key => makeOption(key, optionsConfig, theme))
      : options;

  const hasError = Boolean(meta.touched && meta.error);

  return (
    <SelectBase
      label={label}
      labelId={labelId}
      hasError={hasError}
      errorMessage={meta.error}
      options={displayOptions}
      helperText={helperText}
      {...field}
      {...rest}
    />
  );
};

export default Select;
